/**
 * Menu Burger Frontend Script
 *
 * Gère l'ouverture/fermeture du menu burger et les interactions utilisateur
 * Version modernisée pour la structure hybride (trigger/close fixe + contenu Gutenberg)
 */

class MenuBurger {
	constructor() {
		this.init();
		this.observers = [];
	}

	/**
	 * Détermine si l'affichage est en mode desktop
	 * @return {boolean} True si l'écran est >= 992px
	 */
	static isDesktop() {
		return window.innerWidth >= 992; // 992px est le seuil pour desktop
	}

	/**
	 * Initialise le menu burger
	 */
	init() {
		// Attendre que le DOM soit chargé
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", () => this.bindEvents());
		} else {
			this.bindEvents();
		}

		// Ajouter un écouteur pour le redimensionnement de fenêtre
		window.addEventListener("resize", this.handleResize.bind(this));
	}

	/**
	 * Gère le redimensionnement de la fenêtre
	 */
	handleResize() {
		// Mettre à jour l'affichage des sous-menus en fonction de la taille d'écran
		this.updateSubmenuDisplay();
	}

	/**
	 * Met à jour l'affichage des sous-menus en fonction de la taille d'écran
	 */
	updateSubmenuDisplay() {
		// Réinitialiser les sous-menus ouverts lors du changement de mode
		const openSubmenus = document.querySelectorAll(
			".wp-block-navigation__submenu-container.open",
		);
		openSubmenus.forEach((submenu) => {
			submenu.classList.remove("open");
			const parent =
				submenu.closest(".has-child") ||
				submenu.closest(".wp-block-navigation-submenu");
			if (parent) {
				parent.classList.remove("menu-open");
			}
		});

		// Mettre à jour l'affichage des liens de retour
		const backLinks = document.querySelectorAll(".menu-burger .back-link");
		backLinks.forEach((link) => {
			if (MenuBurger.isDesktop()) {
				link.style.display = "none";
			} else {
				link.style.display = "";
			}
		});

		// Correction : masquer le conteneur desktop-submenu-container en mobile
		const isDesktop = MenuBurger.isDesktop();
		document
			.querySelectorAll(".desktop-submenu-container")
			.forEach((container) => {
				if (!isDesktop) {
					container.classList.remove("active");
					container.innerHTML = "";
				}
			});
	}
	/**
	 * Attache les événements aux éléments du menu
	 */
	bindEvents() {
		// Sélectionner tous les menus burger sur la page
		const menuBurgers = document.querySelectorAll(".menu-burger");

		menuBurgers.forEach((menuBurger) => {
			this.initMenuBurger(menuBurger);
		});

		// Initialiser les sous-menus
		this.initSubmenuNavigation();

		// Désactiver l'ouverture au survol
		this.disableNativeHoverBehavior();

		// Configurer le conteneur desktop
		this.setupDesktopSubmenuContainer();

		// Gérer la fermeture avec la touche Escape
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape") {
				this.closeAllMenus();
			}
		});

		// Masquer les groupes vides dans le frontend
		this.hideEmptyGroups();

		// Initialiser l'affichage des sous-menus selon la taille d'écran
		this.updateSubmenuDisplay();

		document.querySelectorAll('.wpml-language-switcher-block .has-child, .wpml-language-switcher-block .wp-block-navigation-submenu').forEach(parent => {
			const toggle = parent.querySelector(':scope > .wp-block-navigation-item__content, :scope > .wp-block-navigation-submenu__toggle');
			const submenu = parent.querySelector(':scope > .wp-block-navigation__submenu-container');
			if (toggle && submenu) {
				toggle.addEventListener('click', function(e) {
					e.preventDefault();
					e.stopPropagation();
					const isOpen = submenu.classList.toggle('open');
					toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
					submenu.style.display = isOpen ? 'block' : '';
				});
			}
		});
	}

	/**
	 * Désactive définitivement l'ouverture au survol des sous-menus WordPress
	 */
	disableNativeHoverBehavior() {
    const submenuParents = document.querySelectorAll(
        ".menu-burger .wp-block-navigation-submenu, .menu-burger .has-child"
    );
    const self = this;

    submenuParents.forEach((parent) => {
        const submenuContainer = parent.querySelector(
            ":scope > .wp-block-navigation__submenu-container"
        );

        if (submenuContainer) {
            // NE PAS appliquer de style inline si le sous-menu est en mode below
            if (
                !submenuContainer.classList.contains("open") &&
                !submenuContainer.classList.contains("submenu-below-open")
            ) {
                submenuContainer.style.display = "none";
                submenuContainer.style.visibility = "hidden";
                submenuContainer.style.opacity = "0";
            }

            const observer = new MutationObserver((mutations) => {
                if (submenuContainer._isUpdating) return;
                submenuContainer._isUpdating = true;

                mutations.forEach((mutation) => {
                    if (
                        mutation.attributeName === "class" ||
                        mutation.attributeName === "style"
                    ) {
                        // NE PAS appliquer de style inline si le sous-menu est en mode below
                        if (
                            !submenuContainer.classList.contains("open") &&
                            !submenuContainer.classList.contains("submenu-below-open")
                        ) {
                            submenuContainer.style.display = "none";
                            submenuContainer.style.visibility = "hidden";
                            submenuContainer.style.opacity = "0";
                        }
                    }
                });

                setTimeout(() => {
                    submenuContainer._isUpdating = false;
                }, 0);
            });

            if (self.observers) {
                self.observers.push(observer);
            }

            observer.observe(submenuContainer, {
                attributes: true,
                attributeFilter: ["class", "style"],
            });
        }
    });
}

	/**
	 * Configure le conteneur pour les sous-menus en mode desktop
	 */
	setupDesktopSubmenuContainer() {
		// Ne pas vérifier isDesktop() ici pour s'assurer que le conteneur est toujours créé
		// Le CSS s'occupe de cacher le conteneur en mode mobile

		const menuBurgers = document.querySelectorAll(".menu-burger");

		menuBurgers.forEach((menuBurger) => {
			// Trouver la rangée de navigation
			const navRow = menuBurger.querySelector(".menu-burger-nav-row");
			if (!navRow) return;

			// Supprimer tout conteneur existant pour éviter les doublons
			const existingContainer = navRow.querySelector(
				".desktop-submenu-container",
			);
			if (existingContainer) {
				existingContainer.remove();
			}

			// Créer un nouveau conteneur
			const desktopSubmenuContainer = document.createElement("div");
			desktopSubmenuContainer.className = "desktop-submenu-container";
			navRow.appendChild(desktopSubmenuContainer);

			// Stocker une référence au conteneur
			menuBurger.desktopSubmenuContainer = desktopSubmenuContainer;
		});
	}

	/**
	 * Initialise la navigation des sous-menus
	 */
	initSubmenuNavigation() {
    const menuBurgers = document.querySelectorAll(".menu-burger");
    menuBurgers.forEach((menuBurger) => {
        const submenuOpenMode = menuBurger.dataset.submenuOpenMode || "overlay";
        const submenuOverlayIcon = menuBurger.dataset.submenuOverlayIcon || "";
        const submenuBelowIcon = menuBurger.dataset.submenuBelowIcon || "";

        const submenuParents = Array.from(
    menuBurger.querySelectorAll(".wp-block-navigation-submenu, .has-child")
).filter(parent => !parent.closest('.wpml-language-switcher-block'));

        submenuParents.forEach((parent) => {
            const parentLink = parent.querySelector(":scope > a");
            const submenuContainer = parent.querySelector(
                ":scope > .wp-block-navigation__submenu-container"
            );

            // Ajout de l'icône sur le lien parent si non déjà présent
            if (parentLink && !parentLink.querySelector(".submenu-icon")) {
                const iconHTML = submenuOpenMode === "below" ? submenuBelowIcon : submenuOverlayIcon;
                let iconToInsert = "";
                if (iconHTML) {
                    if (/^https?:\/\//.test(iconHTML)) {
                        iconToInsert = `<img src="${iconHTML}" alt="" class="submenu-icon-img" />`;
                    } else {
                        iconToInsert = iconHTML;
                    }
                    parentLink.insertAdjacentHTML("beforeend", `<span class="submenu-icon">${iconToInsert}</span>`);
                }
            }

            if (submenuContainer && parentLink) {
                submenuContainer.classList.add("has-back-link");
                const parentText = parentLink.textContent;
                const parentUrl = parentLink.getAttribute("href");

                // Ajout du lien retour et du lien dupliqué
                if (!submenuContainer.querySelector(".back-link") && submenuOpenMode !== "below") {
                    const backLink = document.createElement("li");
                    backLink.className = "wp-block-navigation-item back-link";
                    if (MenuBurger.isDesktop()) backLink.style.display = "none";
                    backLink.innerHTML = `
                        <a class="wp-block-navigation-item__content">
                            <span class="back-link-arrow">←</span> Retour
                        </a>
                    `;
                    const duplicateParentLink = document.createElement("li");
                    duplicateParentLink.className = "wp-block-navigation-item duplicate-parent";
                    duplicateParentLink.innerHTML = `
                        <a href="${parentUrl}" class="wp-block-navigation-item__content">
                            ${parentText}
                        </a>
                    `;
                    submenuContainer.insertBefore(duplicateParentLink, submenuContainer.firstChild);
                    submenuContainer.insertBefore(backLink, submenuContainer.firstChild);

                    backLink.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        submenuContainer.classList.remove("open");
                        parent.classList.remove("menu-open");
                    });
                } else if (!submenuContainer.querySelector(".duplicate-parent")) {
                    const duplicateParentLink = document.createElement("li");
                    duplicateParentLink.className = "wp-block-navigation-item duplicate-parent";
                    duplicateParentLink.innerHTML = `
                        <a href="${parentUrl}" class="wp-block-navigation-item__content">
                            ${parentText}
                        </a>
                    `;
                    submenuContainer.insertBefore(duplicateParentLink, submenuContainer.firstChild);
                }

                // Gestion du clic sur le lien parent
                parentLink.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Fermer les autres sous-menus ouverts
                    submenuParents.forEach((otherParent) => {
                        if (otherParent !== parent) {
                            const otherSubmenu = otherParent.querySelector(
                                ":scope > .wp-block-navigation__submenu-container"
                            );
                            if (otherSubmenu?.classList.contains("open")) {
                                otherSubmenu.classList.remove("open");
                                otherParent.classList.remove("menu-open");
                                if (submenuOpenMode === "below") {
                                    otherSubmenu.classList.remove("submenu-below-open");
                                }
                            }
                        }
                    });

                    // Ouvre/ferme le sous-menu actuel
                    submenuContainer.classList.toggle("open");
                    parent.classList.toggle("menu-open");

                    // Gestion du mode "below" par classe CSS
                    if (submenuOpenMode === "below") {
                        if (submenuContainer.classList.contains("open")) {
                            submenuContainer.classList.add("submenu-below-open");
                        } else {
                            submenuContainer.classList.remove("submenu-below-open");
                        }
                        return; // Ne pas exécuter le code overlay
                    }

                    // Gestion du mode desktop (overlay)
                    if (MenuBurger.isDesktop()) {
                        const menuBurger = parent.closest(".menu-burger");
                        if (!menuBurger || !menuBurger.desktopSubmenuContainer) return;
                        const desktopContainer = menuBurger.desktopSubmenuContainer;

                        if (submenuContainer.classList.contains("open")) {
                            desktopContainer.classList.add("active");
                            desktopContainer.innerHTML = "";
                            Array.from(submenuContainer.children).forEach((child) => {
                                if (!child.classList.contains("back-link")) {
                                    const clone = child.cloneNode(true);
                                    desktopContainer.appendChild(clone);
                                }
                            });
                            const clonedLinks = desktopContainer.querySelectorAll("a");
                            clonedLinks.forEach((link) => {
                                link.addEventListener("click", (event) => {
                                    if (!link.parentElement.classList.contains("duplicate-parent")) {
                                        return true;
                                    }
                                });
                            });
                            const menuOverlay = parent.closest(".menu-burger__overlay");
                            if (menuOverlay) {
                                let bgColor = null;
                                const menuContent = menuOverlay.querySelector(".menu-burger__content");
                                if (menuContent) bgColor = getComputedStyle(menuContent).backgroundColor;
                                if (!bgColor || bgColor === "rgba(0, 0, 0, 0)" || bgColor === "transparent") {
                                    const navRow = menuOverlay.querySelector(".menu-burger-nav-row");
                                    if (navRow) bgColor = getComputedStyle(navRow).backgroundColor;
                                }
                                if (!bgColor || bgColor === "rgba(0, 0, 0, 0)" || bgColor === "transparent") {
                                    bgColor = getComputedStyle(menuOverlay).backgroundColor;
                                }
                                if (!bgColor || bgColor === "rgba(0, 0, 0, 0)" || bgColor === "transparent") {
                                    bgColor = "#ffffff";
                                }
                                desktopContainer.style.backgroundColor = bgColor;
                            }
                            submenuContainer.style.cssText =
                                "display: none !important; visibility: hidden !important; opacity: 0 !important;";
                            if (submenuContainer._hideObserver) {
                                submenuContainer._hideObserver.disconnect();
                                submenuContainer._hideObserver = null;
                            }
                            submenuContainer._hideObserver = new MutationObserver(
                                (mutations) => {
                                    if (submenuContainer._isUpdatingStyle) return;
                                    submenuContainer._isUpdatingStyle = true;
                                    if (
                                        MenuBurger.isDesktop() &&
                                        submenuContainer.classList.contains("open")
                                    ) {
                                        submenuContainer.style.cssText =
                                            "display: none !important; visibility: hidden !important; opacity: 0 !important;";
                                    }
                                    setTimeout(() => {
                                        submenuContainer._isUpdatingStyle = false;
                                    }, 0);
                                }
                            );
                            if (this.observers) {
                                this.observers.push(submenuContainer._hideObserver);
                            }
                            submenuContainer._hideObserver.observe(submenuContainer, {
                                attributes: true,
                                attributeFilter: ["style", "class"],
                            });
                        } else {
                            desktopContainer.classList.remove("active");
                            desktopContainer.innerHTML = "";
                            submenuContainer.style.cssText = "";
                            if (submenuContainer._hideObserver) {
                                submenuContainer._hideObserver.disconnect();
                                submenuContainer._hideObserver = null;
                            }
                        }
                    } else {
                        // En mode mobile, on veut montrer le sous-menu original
                        submenuContainer.style.cssText = "";
                        if (submenuContainer.classList.contains("open")) {
                            const menuOverlay = parent.closest(".menu-burger__overlay");
                            if (menuOverlay) {
                                const bgColor = getComputedStyle(menuOverlay).backgroundColor;
                                submenuContainer.style.backgroundColor = bgColor;
                            }
                        }
                    }
                });
            }
        });
    });
}
	/**
	 * Masque les groupes vides
	 */
	hideEmptyGroups() {
		const emptyGroups = document.querySelectorAll(".wp-block-group.is-empty");
		emptyGroups.forEach((group) => {
			// Vérifier si le groupe est vraiment vide (pas d'enfants visibles)
			const hasVisibleContent = Array.from(group.children).some((child) => {
				return child.offsetHeight > 0 || child.offsetWidth > 0;
			});

			if (!hasVisibleContent) {
				group.style.display = "none";
			}
		});
	}

	/**
	 * Initialise un menu burger spécifique
	 * @param {HTMLElement} menuBurger - L'élément de menu burger à initialiser
	 */
	initMenuBurger(menuBurger) {
		const overlay = menuBurger.querySelector(".menu-burger__overlay");
		const trigger = menuBurger.querySelector(".menu-burger__trigger");

		if (!overlay || !trigger) {
			console.warn("Menu burger: Éléments manquants", { overlay, trigger });
			return;
		}

		const menuId = overlay.getAttribute("id");
		const self = this;

		// Event listener pour l'icône d'ouverture
		trigger.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			self.openMenu(overlay, menuId);
		});

		// Support clavier pour l'accessibilité
		trigger.addEventListener("keydown", (e) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				e.stopPropagation();
				self.openMenu(overlay, menuId);
			}
		});

		// Chercher les icônes de fermeture dans l'overlay
		const closeButton = overlay.querySelector(".menu-burger__close");
		const closeIcon = overlay.querySelector(".menu-burger__close-icon");

		// Nouvelle logique : chercher les images dans la rangée supérieure
		const topRowImages = overlay.querySelectorAll(
			".menu-burger-top-row .wp-block-image img",
		);

		// Event listener pour l'icône de fermeture fixe (ancien système)
		if (closeButton) {
			closeButton.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				self.closeMenu(overlay, menuId);
			});

			// Support clavier pour l'accessibilité
			closeButton.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					e.stopPropagation();
					self.closeMenu(overlay, menuId);
				}
			});
		}

		// Event listener pour l'icône de fermeture PHP (ancien système)
		if (closeIcon) {
			closeIcon.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				self.closeMenu(overlay, menuId);
			});

			// Support clavier pour l'accessibilité
			closeIcon.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					e.stopPropagation();
					self.closeMenu(overlay, menuId);
				}
			});
		}

		// Nouveau système : Event listeners pour les images dans la rangée supérieure
		topRowImages.forEach((image) => {
			// Ajouter les attributs d'accessibilité
			image.setAttribute("role", "button");
			image.setAttribute("tabindex", "0");
			image.setAttribute("aria-label", "Fermer le menu");

			// Event listener pour le clic
			image.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				self.closeMenu(overlay, menuId);
			});

			// Support clavier pour l'accessibilité
			image.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					e.stopPropagation();
					self.closeMenu(overlay, menuId);
				}
			});
		});

		// Event listener pour fermer en cliquant sur l'overlay
		overlay.addEventListener("click", (e) => {
			// Fermer seulement si on clique directement sur l'overlay, pas sur le contenu
			if (e.target === overlay) {
				self.closeMenu(overlay, menuId);
			}
		});

		// Empêcher la fermeture quand on clique sur le contenu
		const content = overlay.querySelector(".menu-burger__content");
		if (content) {
			content.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		}

		// Empêcher la fermeture quand on clique sur les groupes
		const contentGroups = overlay.querySelectorAll(".wp-block-group");
		contentGroups.forEach((group) => {
			group.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		});
	}

	/**
	 * Ouvre un menu burger
	 * @param {HTMLElement} overlay - L'élément overlay du menu
	 * @param {string} menuId - L'identifiant du menu
	 */
	openMenu(overlay, menuId) {
		// Fermer tous les autres menus ouverts
		this.closeAllMenus();

		// Toujours remettre display:block avant d'ajouter la classe d'ouverture
		overlay.style.display = "block";
		// Forcer le reflow pour que l'animation démarre bien
		void overlay.offsetWidth;

		overlay.classList.add("menu-burger__overlay--open");
		overlay.setAttribute("aria-hidden", "false");

		// Bloquer le scroll du body
		document.body.classList.add("menu-burger-open");
		document.body.style.overflow = "hidden";

		// Forcer l'affichage de la navigation WordPress
		this.forceNavigationDisplay(overlay);

		const headerMobile = document.querySelector(
			".header-responsive__container--mobile",
		);
		if (headerMobile) {
			// Sauvegarder la valeur initiale une seule fois
			if (typeof headerMobile._oldPosition === "undefined") {
				headerMobile._oldPosition = headerMobile.style.position;
			}
			if (typeof headerMobile._oldZIndex === "undefined") {
				headerMobile._oldZIndex = headerMobile.style.zIndex;
			}
			headerMobile.style.position = "relative";
			headerMobile.style.zIndex = "auto";
			headerMobile.classList.add("header-mobile--menu-open");
		}

		// Focus sur le premier élément focusable dans le menu
		setTimeout(() => {
			const firstFocusable = overlay.querySelector(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);
			if (firstFocusable) {
				firstFocusable.focus();
			}
		}, 100);

		// Émettre un événement personnalisé
		const event = new CustomEvent("menuBurgerOpen", {
			detail: { menuId, overlay },
		});
		document.dispatchEvent(event);
	}

	/**
	 * Force l'affichage de la navigation WordPress
	 * @param {HTMLElement} overlay - L'élément overlay du menu
	 */
	forceNavigationDisplay(overlay) {
		// Trouver toutes les navigations dans l'overlay
		const navigations = overlay.querySelectorAll(".wp-block-navigation");

		navigations.forEach((nav) => {
			// Forcer l'affichage de la navigation
			nav.style.display = "flex";
			nav.style.visibility = "visible";
			nav.style.opacity = "1";

			// Si c'est une navigation responsive, forcer l'affichage du contenu
			if (nav.classList.contains("is-responsive")) {
				const responsiveContainer = nav.querySelector(
					".wp-block-navigation__responsive-container",
				);
				const responsiveContent = nav.querySelector(
					".wp-block-navigation__responsive-container-content",
				);
				const navContainer = nav.querySelector(
					".wp-block-navigation__container",
				);

				if (responsiveContainer) {
					responsiveContainer.style.display = "flex";
					responsiveContainer.style.position = "static";
					responsiveContainer.style.opacity = "1";
					responsiveContainer.style.visibility = "visible";
				}

				if (responsiveContent) {
					responsiveContent.style.display = "flex";
					responsiveContent.style.position = "static";
				}

				if (navContainer) {
					navContainer.style.display = "flex";
					navContainer.style.visibility = "visible";
					navContainer.style.opacity = "1";
				}
			}

			// Forcer l'affichage du container principal
			const mainContainer = nav.querySelector(
				".wp-block-navigation__container",
			);
			if (mainContainer) {
				mainContainer.style.display = "flex";
				mainContainer.style.flexDirection = "column";
			}
		});
	}

	/**
	 * Ferme un menu burger
	 * @param {HTMLElement} overlay - L'élément overlay du menu
	 * @param {string} menuId - L'identifiant du menu
	 */
	closeMenu(overlay, menuId) {
		// Si déjà en train de fermer, ne rien faire
		if (overlay.classList.contains("menu-burger__overlay--closing")) return;
		if (!overlay.classList.contains("menu-burger__overlay--open")) return;

		overlay.classList.add("menu-burger__overlay--closing");
		overlay.classList.remove("menu-burger__overlay--open");

		const onAnimationEnd = () => {
			overlay.classList.remove("menu-burger__overlay--closing");
			overlay.setAttribute("aria-hidden", "true");
			overlay.style.display = "none";

			// Débloquer le scroll du body
			document.body.classList.remove("menu-burger-open");
			document.body.style.overflow = "";

			const headerMobile = document.querySelector(
				".header-responsive__container--mobile",
			);
			if (headerMobile) {
				// Restaurer exactement la valeur initiale
				headerMobile.style.position = headerMobile._oldPosition || "";
				headerMobile.style.zIndex = headerMobile._oldZIndex || "";
				headerMobile.classList.remove("header-mobile--menu-open");
			}

			// Émettre un événement personnalisé
			const event = new CustomEvent("menuBurgerClose", {
				detail: { menuId, overlay },
			});
			document.dispatchEvent(event);

			// Nettoyer l'eventListener pour éviter les doublons
			overlay.removeEventListener("animationend", onAnimationEnd);
		};

		// Forcer l'affichage pour permettre l'animation si besoin
		overlay.style.display = "block";

		overlay.addEventListener("animationend", onAnimationEnd, { once: true });
	}

	/**
	 * Ferme tous les menus burger ouverts
	 */
	closeAllMenus() {
		const openOverlays = document.querySelectorAll(
			".menu-burger__overlay--open",
		);
		openOverlays.forEach((overlay) => {
			const menuId = overlay.getAttribute("id");
			this.closeMenu(overlay, menuId);
		});
	}

	/**
	 * Méthode publique pour ouvrir un menu spécifique
	 * @param {string} menuId - L'identifiant du menu à ouvrir
	 */
	static openMenuById(menuId) {
		const overlay = document.getElementById(menuId);
		if (overlay) {
			const instance = new MenuBurger();
			instance.openMenu(overlay, menuId);
		}
	}

	/**
	 * Méthode publique pour fermer un menu spécifique
	 * @param {string} menuId - L'identifiant du menu à fermer
	 */
	static closeMenuById(menuId) {
		const overlay = document.getElementById(menuId);
		if (overlay) {
			const instance = new MenuBurger();
			instance.closeMenu(overlay, menuId);
		}
	}
}

// Initialiser le menu burger quand le script est chargé
new MenuBurger();

// Exposer la classe globalement pour permettre l'utilisation externe
window.MenuBurger = MenuBurger;
